import React, { useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface VCProfileEditDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: 'overview' | 'firm' | 'investment' | 'contact';
  data: any;
  onSave: (updatedData: any) => void;
}

const VCProfileEditDrawer: React.FC<VCProfileEditDrawerProps> = ({
  open,
  onOpenChange,
  section,
  data,
  onSave
}) => {
  const [formData, setFormData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await onSave(formData);
      toast.success('Profile updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const renderOverviewFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="display_name">Display Name</Label>
        <Input
          id="display_name"
          value={formData.display_name || ''}
          onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="bio">Bio</Label>
        <Textarea
          id="bio"
          value={formData.bio || ''}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          value={formData.location || ''}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
        />
      </div>
    </>
  );

  const renderFirmFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="firm_name">Firm Name</Label>
        <Input
          id="firm_name"
          value={formData.venture_capital?.firm_name || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, firm_name: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="partner_name">Partner Name</Label>
        <Input
          id="partner_name"
          value={formData.venture_capital?.partner_name || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, partner_name: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="partner_title">Partner Title</Label>
        <Input
          id="partner_title"
          value={formData.venture_capital?.partner_title || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, partner_title: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="aum">Assets Under Management</Label>
        <Input
          id="aum"
          type="number"
          value={formData.venture_capital?.aum || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, aum: parseFloat(e.target.value) || 0 }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="portfolio_companies">Portfolio Companies</Label>
        <Input
          id="portfolio_companies"
          type="number"
          value={formData.venture_capital?.portfolio_companies || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, portfolio_companies: parseInt(e.target.value) || 0 }
          })}
        />
      </div>
    </>
  );

  const renderInvestmentFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="ticket_size_min">Min Ticket Size</Label>
        <Input
          id="ticket_size_min"
          type="number"
          value={formData.venture_capital?.ticket_size_min || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, ticket_size_min: parseFloat(e.target.value) || 0 }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ticket_size_max">Max Ticket Size</Label>
        <Input
          id="ticket_size_max"
          type="number"
          value={formData.venture_capital?.ticket_size_max || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, ticket_size_max: parseFloat(e.target.value) || 0 }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="investment_thesis">Investment Thesis</Label>
        <Textarea
          id="investment_thesis"
          value={formData.venture_capital?.investment_thesis || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, investment_thesis: e.target.value }
          })}
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="decision_timeline">Decision Timeline</Label>
        <Input
          id="decision_timeline"
          value={formData.venture_capital?.decision_timeline || ''}
          onChange={(e) => setFormData({
            ...formData,
            venture_capital: { ...formData.venture_capital, decision_timeline: e.target.value }
          })}
        />
      </div>
    </>
  );

  const renderContactFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
      {formData.venture_capital && (
        <>
          <div className="space-y-2">
            <Label htmlFor="partner_email">Partner Email</Label>
            <Input
              id="partner_email"
              type="email"
              value={formData.venture_capital.partner_email || ''}
              onChange={(e) => setFormData({
                ...formData,
                venture_capital: { ...formData.venture_capital, partner_email: e.target.value }
              })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="partner_phone">Partner Phone</Label>
            <Input
              id="partner_phone"
              value={formData.venture_capital.partner_phone || ''}
              onChange={(e) => setFormData({
                ...formData,
                venture_capital: { ...formData.venture_capital, partner_phone: e.target.value }
              })}
            />
          </div>
        </>
      )}
    </>
  );

  const getSectionTitle = () => {
    switch (section) {
      case 'overview': return 'Edit Overview';
      case 'firm': return 'Edit Firm Details';
      case 'investment': return 'Edit Investment Criteria';
      case 'contact': return 'Edit Contact Info';
      default: return 'Edit Profile';
    }
  };

  const renderFields = () => {
    switch (section) {
      case 'overview': return renderOverviewFields();
      case 'firm': return renderFirmFields();
      case 'investment': return renderInvestmentFields();
      case 'contact': return renderContactFields();
      default: return null;
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <div className="flex items-center justify-between">
            <DrawerTitle>{getSectionTitle()}</DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <div className="px-4 pb-4 space-y-4">
          {renderFields()}
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={isLoading} className="flex-1">
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default VCProfileEditDrawer;